/**
 * Challenge Store
 *
 * Manages challenge state including browsing, filtering, and user challenges.
 * Provides selectors for common access patterns like featured challenges
 * and per-user challenge stats. Also handles vendor challenge creation and
 * submission workflows.
 *
 * @module useChallengeStore
 *
 * @example
 * ```tsx
 * import { useChallengeStore, selectFeatured, selectMyChallengeStats } from '@/store/useChallengeStore'
 *
 * function ChallengeHub() {
 *   const { fetchChallenges, fetchFeaturedChallenges } = useChallengeStore()
 *   const featured = useChallengeStore(selectFeatured)
 *   const stats = useChallengeStore(selectMyChallengeStats)
 *
 *   useEffect(() => {
 *     fetchChallenges()
 *     fetchFeaturedChallenges()
 *   }, [])
 *
 *   return <FeaturedCarousel challenges={featured} inProgressCount={stats.inProgress} />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  Challenge,
  ChallengeCategory,
  ChallengeDiscussion,
  UserChallenge,
  UserChallengeStatus,
  ChallengeFilters,
  ChallengeSortBy,
  MyChallengesTab,
} from '../types'
import { challengeApi } from '../api'
import type { ChallengeGalleryItem } from '../api/challenge.api'

interface ChallengeState {
  // Browse/Explore state
  challenges: Challenge[]
  categories: ChallengeCategory[]
  featuredChallenges: Challenge[]
  filters: ChallengeFilters
  sortBy: ChallengeSortBy
  currentPage: number
  totalPages: number
  hasMore: boolean

  // Vendor's challenges
  vendorChallenges: Challenge[]

  // Current challenge (detail view)
  currentChallenge: Challenge | null

  // My Challenges state
  myChallenges: UserChallenge[]
  currentTab: MyChallengesTab
  myChallengeStats: {
    invitations: number
    fromOthers: number
    inProgress: number
    inReview: number
    completed: number
    unverified: number
    declined: number
  }

  // Challenge status map (challengeId → status) for the current user
  myChallengeStatusMap: Record<string, UserChallengeStatus>

  // Discussion & Gallery state
  discussionPosts: ChallengeDiscussion[]
  galleryItems: ChallengeGalleryItem[]
  isLoadingDiscussion: boolean
  isLoadingGallery: boolean

  // Loading states
  isLoading: boolean
  isLoadingCategories: boolean
  isLoadingMyChallenge: boolean

  // Error state
  error: string | null

  // Actions
  fetchChallenges: (page?: number) => Promise<void>
  fetchCategories: () => Promise<void>
  fetchFeaturedChallenges: () => Promise<void>
  fetchChallengeById: (id: string) => Promise<void>
  fetchMyChallenges: (tab?: MyChallengesTab) => Promise<void>
  fetchMyChallengeStats: () => Promise<void>
  setFilters: (filters: Partial<ChallengeFilters>) => void
  setSortBy: (sortBy: ChallengeSortBy) => void
  setCurrentTab: (tab: MyChallengesTab) => void
  fetchMyChallengeStatusMap: () => Promise<void>
  startChallenge: (challengeId: string) => Promise<boolean>
  acceptInvitation: (userChallengeId: string) => Promise<boolean>
  declineInvitation: (userChallengeId: string) => Promise<boolean>
  abandonChallenge: (userChallengeId: string) => Promise<boolean>
  fetchVendorChallenges: () => Promise<void>
  createChallenge: (data: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'completionCount' | 'trackRecordCount' | 'isFeatured'>) => Promise<Challenge | null>
  updateChallenge: (id: string, data: Partial<Challenge>) => Promise<Challenge | null>
  submitForReview: (challengeId: string) => Promise<boolean>
  fetchDiscussionPosts: (challengeId: string) => Promise<void>
  createDiscussionPost: (challengeId: string, content: string, parentId?: string) => Promise<void>
  editDiscussionPost: (postId: string, content: string) => Promise<void>
  deleteDiscussionPost: (postId: string) => Promise<void>
  flagDiscussionPost: (postId: string) => Promise<void>
  fetchChallengeGallery: (challengeId: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  challenges: [],
  categories: [],
  featuredChallenges: [],
  filters: {},
  sortBy: 'newest' as ChallengeSortBy,
  currentPage: 1,
  totalPages: 1,
  hasMore: false,
  vendorChallenges: [],
  currentChallenge: null,
  myChallenges: [],
  myChallengeStatusMap: {} as Record<string, UserChallengeStatus>,
  currentTab: 'in_progress' as MyChallengesTab,
  myChallengeStats: {
    invitations: 0,
    fromOthers: 0,
    inProgress: 0,
    inReview: 0,
    completed: 0,
    unverified: 0,
    declined: 0,
  },
  discussionPosts: [],
  galleryItems: [],
  isLoadingDiscussion: false,
  isLoadingGallery: false,
  isLoading: false,
  isLoadingCategories: false,
  isLoadingMyChallenge: false,
  error: null,
}

export const useChallengeStore = createWithEqualityFn<ChallengeState>()((set, get) => ({
  ...initialState,

  fetchChallenges: async (page = 1) => {
    set({ isLoading: true, error: null })

    const { filters, sortBy } = get()
    const response = await challengeApi.getChallenges(filters, sortBy, page)

    if (response.success && response.data) {
      set({
        challenges: page === 1 ? response.data : [...get().challenges, ...response.data],
        currentPage: response.pagination?.page ?? 1,
        totalPages: response.pagination?.totalPages ?? 1,
        hasMore: response.pagination?.hasMore ?? false,
        isLoading: false,
      })
    } else {
      set({
        error: response.error ?? 'Failed to load challenges',
        isLoading: false,
      })
    }
  },

  fetchCategories: async () => {
    set({ isLoadingCategories: true })

    const response = await challengeApi.getCategories()

    if (response.success && response.data) {
      set({ categories: response.data, isLoadingCategories: false })
    } else {
      set({ isLoadingCategories: false })
    }
  },

  fetchFeaturedChallenges: async () => {
    const response = await challengeApi.getFeaturedChallenges()

    if (response.success && response.data) {
      set({ featuredChallenges: response.data })
    }
  },

  fetchChallengeById: async (id) => {
    set({ isLoading: true, error: null })

    const response = await challengeApi.getChallengeById(id)

    if (response.success) {
      set({ currentChallenge: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Challenge not found',
        isLoading: false,
      })
    }
  },

  fetchMyChallenges: async (tab) => {
    const currentTab = tab ?? get().currentTab
    set({ isLoadingMyChallenge: true, error: null, currentTab })

    const response = await challengeApi.getMyChallengesByTab(currentTab)

    if (response.success && response.data) {
      set({ myChallenges: response.data, isLoadingMyChallenge: false })
    } else {
      set({
        error: response.error ?? 'Failed to load challenges',
        isLoadingMyChallenge: false,
      })
    }
  },

  fetchMyChallengeStats: async () => {
    const response = await challengeApi.getMyChallengeStats()

    if (response.success && response.data) {
      set({ myChallengeStats: response.data })
    }
  },

  setFilters: (filters) => {
    set({ filters: { ...get().filters, ...filters } })
    get().fetchChallenges(1)
  },

  setSortBy: (sortBy) => {
    set({ sortBy })
    get().fetchChallenges(1)
  },

  setCurrentTab: (tab) => {
    get().fetchMyChallenges(tab)
  },

  fetchMyChallengeStatusMap: async () => {
    const response = await challengeApi.getMyChallengeStatusMap()
    if (response.success && response.data) {
      set({ myChallengeStatusMap: response.data })
    }
  },

  startChallenge: async (challengeId) => {
    set({ isLoadingMyChallenge: true, error: null })

    const response = await challengeApi.startChallenge(challengeId)

    if (response.success) {
      // Refresh stats and my challenges
      await get().fetchMyChallengeStats()
      set({ isLoadingMyChallenge: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to start challenge',
        isLoadingMyChallenge: false,
      })
      return false
    }
  },

  acceptInvitation: async (userChallengeId) => {
    set({ isLoadingMyChallenge: true, error: null })

    const response = await challengeApi.acceptInvitation(userChallengeId)

    if (response.success) {
      await get().fetchMyChallengeStats()
      await get().fetchMyChallenges()
      set({ isLoadingMyChallenge: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to accept invitation',
        isLoadingMyChallenge: false,
      })
      return false
    }
  },

  declineInvitation: async (userChallengeId) => {
    set({ isLoadingMyChallenge: true, error: null })

    const response = await challengeApi.declineInvitation(userChallengeId)

    if (response.success) {
      await get().fetchMyChallengeStats()
      await get().fetchMyChallenges()
      set({ isLoadingMyChallenge: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to decline invitation',
        isLoadingMyChallenge: false,
      })
      return false
    }
  },

  abandonChallenge: async (userChallengeId) => {
    set({ isLoadingMyChallenge: true, error: null })

    const response = await challengeApi.abandonChallenge(userChallengeId)

    if (response.success) {
      await get().fetchMyChallengeStats()
      await get().fetchMyChallenges()
      set({ isLoadingMyChallenge: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to abandon challenge',
        isLoadingMyChallenge: false,
      })
      return false
    }
  },

  fetchVendorChallenges: async () => {
    set({ isLoading: true, error: null })

    const response = await challengeApi.getVendorChallenges()

    if (response.success && response.data) {
      set({ vendorChallenges: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load vendor challenges',
        isLoading: false,
      })
    }
  },

  createChallenge: async (data) => {
    set({ isLoading: true, error: null })

    const response = await challengeApi.createChallenge(data)

    if (response.success && response.data) {
      set((state) => ({
        vendorChallenges: [response.data!, ...state.vendorChallenges],
        isLoading: false,
      }))
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create challenge',
        isLoading: false,
      })
      return null
    }
  },

  updateChallenge: async (id, data) => {
    set({ isLoading: true, error: null })

    const response = await challengeApi.updateChallenge(id, data)

    if (response.success && response.data) {
      set((state) => ({
        vendorChallenges: state.vendorChallenges.map((c) =>
          c.id === id ? { ...c, ...response.data! } : c
        ),
        currentChallenge: state.currentChallenge?.id === id
          ? { ...state.currentChallenge, ...response.data! }
          : state.currentChallenge,
        isLoading: false,
      }))
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to update challenge',
        isLoading: false,
      })
      return null
    }
  },

  fetchDiscussionPosts: async (challengeId) => {
    set({ isLoadingDiscussion: true })

    const response = await challengeApi.getDiscussionPosts(challengeId)

    if (response.success && response.data) {
      set({ discussionPosts: response.data, isLoadingDiscussion: false })
    } else {
      set({ isLoadingDiscussion: false })
    }
  },

  createDiscussionPost: async (challengeId, content, parentId) => {
    const response = await challengeApi.createDiscussionPost(challengeId, content, parentId)

    if (response.success && response.data) {
      set((state) => ({
        discussionPosts: [...state.discussionPosts, response.data!],
      }))
    }
  },

  editDiscussionPost: async (postId, content) => {
    const response = await challengeApi.editDiscussionPost(postId, content)

    if (response.success && response.data) {
      set((state) => ({
        discussionPosts: state.discussionPosts.map((p) =>
          p.id === postId ? response.data! : p
        ),
      }))
    }
  },

  deleteDiscussionPost: async (postId) => {
    const response = await challengeApi.deleteDiscussionPost(postId)

    if (response.success) {
      set((state) => ({
        discussionPosts: state.discussionPosts.filter((p) => p.id !== postId),
      }))
    }
  },

  flagDiscussionPost: async (postId) => {
    const response = await challengeApi.flagDiscussionPost(postId)

    if (response.success && response.data) {
      set((state) => ({
        discussionPosts: state.discussionPosts.map((p) =>
          p.id === postId ? response.data! : p
        ),
      }))
    }
  },

  fetchChallengeGallery: async (challengeId) => {
    set({ isLoadingGallery: true })

    const response = await challengeApi.getChallengeGallery(challengeId)

    if (response.success && response.data) {
      set({ galleryItems: response.data, isLoadingGallery: false })
    } else {
      set({ isLoadingGallery: false })
    }
  },

  submitForReview: async (challengeId) => {
    set({ isLoading: true, error: null })

    const response = await challengeApi.submitForReview(challengeId)

    if (response.success && response.data) {
      // Update the challenge status in vendorChallenges
      set((state) => ({
        vendorChallenges: state.vendorChallenges.map((c) =>
          c.id === challengeId ? { ...c, status: 'pending_review' as const } : c
        ),
        isLoading: false,
      }))
      return true
    } else {
      set({
        error: response.error ?? 'Failed to submit for review',
        isLoading: false,
      })
      return false
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select the full list of browsable challenges. */
export const selectChallenges = (state: ChallengeState) => state.challenges

/** Select all challenge categories for filter UIs. */
export const selectCategories = (state: ChallengeState) => state.categories

/** Select the featured challenges shown on the home/explore page. */
export const selectFeatured = (state: ChallengeState) => state.featuredChallenges

/** Select the current user's challenges (filtered by active tab). */
export const selectMyChallenges = (state: ChallengeState) => state.myChallenges

/**
 * Select challenge stats for the current user (counts by status).
 *
 * @example
 * ```ts
 * const stats = useChallengeStore(selectMyChallengeStats)
 * // { invitations: 2, inProgress: 5, completed: 12, ... }
 * ```
 */
export const selectMyChallengeStats = (state: ChallengeState) => state.myChallengeStats

/** Select challenges owned by the current vendor. */
export const selectVendorChallenges = (state: ChallengeState) => state.vendorChallenges

/** Select the challenge currently being viewed in the detail page. */
export const selectCurrentChallenge = (state: ChallengeState) => state.currentChallenge
